import React, { useState, useEffect } from 'react';
import { Users, Plus, Search, Edit2, Trash2, X, Save, Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';

const ClientNotes = () => {
  const { currentUser } = useAuth();
  const [notes, setNotes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [formData, setFormData] = useState({
    clientName: '',
    petNames: '',
    notes: '',
    importantFlags: [],
    isFavorite: false
  });

  const flagOptions = [
    { id: 'deaf', label: '🦻 Hearing impaired', color: 'bg-blue-100 text-blue-700' },
    { id: 'spanish', label: '🇪🇸 Spanish speaker', color: 'bg-yellow-100 text-yellow-700' },
    { id: 'anxious', label: '😰 Anxious pet', color: 'bg-purple-100 text-purple-700' },
    { id: 'aggressive', label: '⚠️ Aggressive/bites', color: 'bg-red-100 text-red-700' },
    { id: 'elderly', label: '👴 Elderly client', color: 'bg-gray-100 text-gray-700' },
    { id: 'vip', label: '⭐ VIP client', color: 'bg-amber-100 text-amber-700' },
    { id: 'payment', label: '💳 Payment issues', color: 'bg-orange-100 text-orange-700' },
    { id: 'allergies', label: '🚨 Pet allergies', color: 'bg-pink-100 text-pink-700' },
  ];

  // Load notes from localStorage
  useEffect(() => {
    if (currentUser) {
      const savedNotes = localStorage.getItem(`clientNotes_${currentUser.uid}`);
      if (savedNotes) {
        setNotes(JSON.parse(savedNotes));
      }
    }
  }, [currentUser]);

  // Save notes to localStorage
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem(`clientNotes_${currentUser.uid}`, JSON.stringify(notes));
    }
  }, [notes, currentUser]);

  const resetForm = () => {
    setFormData({
      clientName: '',
      petNames: '',
      notes: '',
      importantFlags: [],
      isFavorite: false
    });
    setEditingNote(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (note) => {
    setFormData({
      clientName: note.clientName,
      petNames: note.petNames,
      notes: note.notes,
      importantFlags: note.importantFlags || [],
      isFavorite: note.isFavorite || false
    });
    setEditingNote(note);
    setShowAddModal(true);
  };

  const saveNote = () => {
    if (!formData.clientName.trim()) {
      alert('Please enter a client name');
      return;
    }

    if (editingNote) {
      // Update existing
      setNotes(prev => prev.map(note => {
        if (note.id === editingNote.id) {
          return {
            ...note,
            ...formData,
            updatedAt: new Date().toISOString()
          };
        }
        return note;
      }));
    } else {
      // Add new
      const newNote = {
        id: Date.now(),
        ...formData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setNotes(prev => [newNote, ...prev]);
    }

    setShowAddModal(false);
    resetForm();
  };

  const deleteNote = (id) => {
    if (window.confirm('Delete this client note?')) {
      setNotes(prev => prev.filter(note => note.id !== id));
    }
  };

  const toggleFavorite = (id) => {
    setNotes(prev => prev.map(note => {
      if (note.id === id) {
        return { ...note, isFavorite: !note.isFavorite };
      }
      return note;
    }));
  };

  const toggleFlag = (flagId) => {
    setFormData(prev => {
      const flags = prev.importantFlags.includes(flagId)
        ? prev.importantFlags.filter(f => f !== flagId)
        : [...prev.importantFlags, flagId];
      return { ...prev, importantFlags: flags };
    });
  };

  const getFlagDisplay = (flagId) => {
    return flagOptions.find(f => f.id === flagId);
  };

  const filteredNotes = notes.filter(note => {
    const search = searchTerm.toLowerCase();
    return (
      note.clientName.toLowerCase().includes(search) ||
      note.petNames.toLowerCase().includes(search) ||
      note.notes.toLowerCase().includes(search)
    );
  }).sort((a, b) => {
    // Favorites first, then by updated date
    if (a.isFavorite !== b.isFavorite) return b.isFavorite ? 1 : -1;
    return new Date(b.updatedAt) - new Date(a.updatedAt);
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-brand-brown flex items-center">
            <Users className="w-8 h-8 mr-3 text-pastel-pink" />
            Client Notes
          </h1>
          <p className="text-brand-brown/60 mt-1">Remember the little things about clients & pets</p>
        </div>

        {/* Search & Add */}
        <div className="flex space-x-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-brown/40" />
            <input
              type="text"
              placeholder="Search clients or pets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>
          <button
            onClick={openAddModal}
            className="btn-primary flex items-center px-4"
          >
            <Plus className="w-5 h-5 mr-1" />
            <span className="hidden sm:inline">Add Note</span>
          </button>
        </div>

        {/* Notes List */}
        <div className="space-y-3">
          {filteredNotes.length === 0 ? (
            <div className="card p-8 text-center">
              <Users className="w-12 h-12 mx-auto text-brand-brown/20 mb-3" />
              <p className="text-brand-brown/60">
                {searchTerm ? 'No matching clients found' : 'No client notes yet. Add one to get started!'}
              </p>
            </div>
          ) : (
            filteredNotes.map(note => (
              <div
                key={note.id}
                className={`card p-4 ${note.isFavorite ? 'border-2 border-amber-300 bg-amber-50/30' : ''}`}
              >
                {/* Header Row */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => toggleFavorite(note.id)}
                      className="p-1"
                    >
                      <Star className={`w-5 h-5 ${note.isFavorite ? 'text-amber-500 fill-amber-500' : 'text-brand-brown/20'}`} />
                    </button>
                    <div>
                      <h3 className="font-bold text-brand-brown text-lg">{note.clientName}</h3>
                      {note.petNames && (
                        <p className="text-sm text-pastel-purple">🐾 {note.petNames}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => openEditModal(note)}
                      className="p-2 hover:bg-pastel-blue/20 rounded-lg transition-colors"
                    >
                      <Edit2 className="w-4 h-4 text-brand-brown/60" />
                    </button>
                    <button
                      onClick={() => deleteNote(note.id)}
                      className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-brand-brown/40 hover:text-red-500" />
                    </button>
                  </div>
                </div>

                {/* Flags */}
                {note.importantFlags && note.importantFlags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {note.importantFlags.map(flagId => {
                      const flag = getFlagDisplay(flagId);
                      if (!flag) return null;
                      return (
                        <span
                          key={flagId}
                          className={`text-xs px-2 py-1 rounded-full ${flag.color}`}
                        >
                          {flag.label}
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Notes */}
                {note.notes && (
                  <p className="text-brand-brown/80 text-sm whitespace-pre-wrap">{note.notes}</p>
                )}

                {/* Footer */}
                <p className="text-xs text-brand-brown/40 mt-3">
                  Updated {formatDate(note.updatedAt)}
                </p>
              </div>
            ))
          )}
        </div>

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white p-4 border-b flex items-center justify-between">
                <h2 className="text-xl font-bold text-brand-brown">
                  {editingNote ? 'Edit Client Note' : 'Add Client Note'}
                </h2>
                <button
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                {/* Client Name */}
                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-1">
                    Client Name *
                  </label>
                  <input
                    type="text"
                    value={formData.clientName}
                    onChange={(e) => setFormData(prev => ({ ...prev, clientName: e.target.value }))}
                    placeholder="e.g., Mrs. Johnson"
                    className="input w-full"
                  />
                </div>

                {/* Pet Names */}
                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-1">
                    Pet Name(s)
                  </label>
                  <input
                    type="text"
                    value={formData.petNames}
                    onChange={(e) => setFormData(prev => ({ ...prev, petNames: e.target.value }))}
                    placeholder="e.g., Bella, Max"
                    className="input w-full"
                  />
                </div>

                {/* Important Flags */}
                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-2">
                    Quick Flags
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {flagOptions.map(flag => (
                      <button
                        key={flag.id}
                        onClick={() => toggleFlag(flag.id)}
                        className={`text-sm px-3 py-1.5 rounded-full transition-all ${
                          formData.importantFlags.includes(flag.id)
                            ? flag.color + ' ring-2 ring-offset-1 ring-brand-brown/20'
                            : 'bg-gray-100 text-brand-brown/60'
                        }`}
                      >
                        {flag.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Remember important details...&#10;- Prefers afternoon appointments&#10;- Cat hides under chair during exams&#10;- Always asks about dental care"
                    className="input w-full h-32 resize-none"
                  />
                </div>

                {/* Favorite Toggle */}
                <button
                  onClick={() => setFormData(prev => ({ ...prev, isFavorite: !prev.isFavorite }))}
                  className={`w-full p-3 rounded-xl border-2 flex items-center justify-center space-x-2 transition-all ${
                    formData.isFavorite
                      ? 'border-amber-400 bg-amber-50 text-amber-700'
                      : 'border-brand-brown/20 text-brand-brown/60'
                  }`}
                >
                  <Star className={`w-5 h-5 ${formData.isFavorite ? 'fill-amber-500' : ''}`} />
                  <span>{formData.isFavorite ? 'Favorited' : 'Add to Favorites'}</span>
                </button>

                {/* Save Button */}
                <button
                  onClick={saveNote}
                  className="btn-primary w-full py-3 flex items-center justify-center"
                >
                  <Save className="w-5 h-5 mr-2" />
                  {editingNote ? 'Save Changes' : 'Add Client Note'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ClientNotes;
